import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import CountrySelect from "aldoo-ra/CountrySelect"
import { showNotification } from "aldoo-ra/Notification"
import { getBillingDetails, updateBillingDetails } from "aldoo-ra/Store/api"
import { usePromise } from "aldoo-ra/usePromise"

export default function BillingDetailsCard() {
  const [billingData, setBillingData] = useState({
    companyName: "",
    taxId: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    },
  })
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)

  const texts = TextBundle({ id: "aldoo-store" })

  const { result: loadedData, execute, pending: loading } = usePromise(getBillingDetails, false)

  useEffect(() => {
    if (!loadedData) return
    setBillingData({
      companyName: loadedData.companyName || "",
      taxId: loadedData.taxId || "",
      address: {
        line1: loadedData.address?.line1 || "",
        line2: loadedData.address?.line2 || "",
        city: loadedData.address?.city || "",
        state: loadedData.address?.state || "",
        postal_code: loadedData.address?.postal_code || "",
        country: loadedData.address?.country || "",
      },
    })
  }, [loadedData])

  const handleInputChange = (field) => (e) => {
    const value = e.target.value
    if (field.startsWith("address.")) {
      const addressField = field.split(".")[1]
      setBillingData((prev) => ({ ...prev, address: { ...prev.address, [addressField]: value } }))
    } else {
      setBillingData((prev) => ({ ...prev, [field]: value }))
    }
    setHasChanges(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateBillingDetails(billingData)
      showNotification({ message: texts?.billing_details_updated_notification, className: "bg-primary text-white" })
      setHasChanges(false)
      await execute()
    } catch (error) {
      showNotification({ message: error?.message || texts?.billing_details_update_error, className: "bg-error text-white" })
    } finally {
      setSaving(false)
    }
  }

  const field = (label, key, placeholder) => (
    <div className="flex flex-col">
      <Typography variant="label_upper_dim">
        {label}
      </Typography>

      <Input
        variant="snap_compact"
        value={key.startsWith("address.") ? billingData.address[key.split(".")[1]] : billingData[key]}
        onChange={handleInputChange(key)}
        placeholder={placeholder}
        disabled={loading}
        className="w-full"
      />
    </div>
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field(texts?.billing_info_company_name, "companyName", texts?.billing_info_company_placeholder)}
            {field(texts?.billing_info_vat, "taxId", texts?.billing_info_vat_placeholder)}
            {field(texts?.billing_info_address_line1, "address.line1", texts?.billing_info_address_line1_placeholder)}
            {field(texts?.billing_info_address_line2, "address.line2", texts?.billing_info_address_line2_placeholder)}
            {field(texts?.billing_info_city, "address.city", texts?.billing_info_city_placeholder)}
            {field(texts?.billing_info_state, "address.state", texts?.billing_info_state_placeholder)}
            {field(texts?.billing_info_postal_code, "address.postal_code", texts?.billing_info_postal_placeholder)}

            <div className="flex flex-col">
              <Typography variant="label_upper_dim">
                {texts?.billing_info_country}
              </Typography>

              <CountrySelect
                variant = "snap_compact"
                value={billingData.address.country}
                onChange={(countryCode) => {
                  setBillingData((prev) => ({ ...prev, address: { ...prev.address, country: countryCode } }))
                  setHasChanges(true)
                }}
                placeholder={texts?.billing_info_country_placeholder}
                enableResetButton={false}
              />
            </div>
          </div>

      <div className="flex justify-end pt-1">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? texts?.billing_info_cta_saving : texts?.billing_info_cta_save}
        </Button>
      </div>
    </div>
  )
}
